import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { useAuth } from "../context/AuthContext";
import {
  fetchAllCreditBills,
  fetchAllPayments,
  fetchAllCreditAuditNotes,
  masterSaveCreditBill,
  masterDeleteCreditBill,
  masterDeleteCreditPayment,
  masterReplaceCreditPayment,
  mergeAllocationsByBillId,
  masterUpdateCreditAuditNote,
  masterDeleteCreditAuditNote,
  addAuditNoteForCustomer,
  paymentTimestamp,
  paymentDateInputValue,
  creditBillBranch,
  creditPaymentBranch,
} from "../services/creditDueService";
import { useCreditBranchFilter } from "./credit/useCreditBranchFilter";
import CreditBranchSelector from "./credit/CreditBranchSelector";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function isoDateVal(v) {
  if (!v) return "";
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (typeof v === "string") return v.slice(0, 10);
  return "";
}

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtPaymentWhen(p) {
  const d = paymentTimestamp(p);
  if (!d || d.getTime() === 0) return "—";
  return d.toLocaleString("en-IN");
}

function billPartyVoucher(billId, billById) {
  const b = billById.get(billId);
  if (!b) return null;
  const name = (b.customerName || "").trim() || "Customer";
  const v = (b.voucherNo || "").trim() || "—";
  return `${name} · ${v}`;
}

function allocSummary(p, billById) {
  const allocs = Array.isArray(p?.allocations) ? p.allocations : [];
  if (!allocs.length) return "—";
  return allocs
    .map((a) => {
      const label = billPartyVoucher(a.billId, billById) || "Unknown bill";
      return `${label} ₹${round2(a.amount)}`;
    })
    .join(", ");
}

const TABS = [
  { id: "bills", label: "Credit bills" },
  { id: "payments", label: "Payments" },
  { id: "audit", label: "Audit notes" },
];

export default function CreditDueMasterPage() {
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [tab, setTab] = useState("bills");
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [query, setQuery] = useState("");

  const [billModal, setBillModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [noteModal, setNoteModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const [newNote, setNewNote] = useState({
    customerId: "",
    customerName: "",
    note: "",
    lastContactDate: "",
    nextActionDate: "",
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p, n] = await Promise.all([fetchAllCreditBills(), fetchAllPayments(), fetchAllCreditAuditNotes()]);
      p.sort((a, x) => paymentTimestamp(x) - paymentTimestamp(a));
      setBills(b);
      setPayments(p);
      setNotes(n);
    } catch (e) {
      toast.error(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const q = query.trim().toLowerCase();

  const billById = useMemo(() => {
    const m = new Map();
    for (const b of bills) {
      if (b?.id) m.set(b.id, b);
    }
    return m;
  }, [bills]);

  const billsInBranch = useMemo(
    () => bills.filter((b) => creditBillBranch(b) === branch),
    [bills, branch]
  );

  const paymentsInBranch = useMemo(
    () => payments.filter((p) => creditPaymentBranch(p, billById) === branch),
    [payments, branch, billById]
  );

  const customerIdsInBranch = useMemo(() => {
    const s = new Set();
    for (const b of billsInBranch) {
      const cid = String(b.customerId || "").trim();
      if (cid) s.add(cid);
    }
    return s;
  }, [billsInBranch]);

  const notesInBranch = useMemo(
    () => notes.filter((n) => customerIdsInBranch.has(String(n.customerId || "").trim())),
    [notes, customerIdsInBranch]
  );

  const billsFiltered = useMemo(() => {
    if (!q) return billsInBranch;
    return billsInBranch.filter((b) => {
      const hay = `${b.id} ${b.customerId} ${b.voucherNo} ${b.customerName} ${b.phone}`.toLowerCase();
      return hay.includes(q);
    });
  }, [billsInBranch, q]);

  const billsFilteredTotals = useMemo(() => {
    let totalAmount = 0;
    let paidAmount = 0;
    let balanceAmount = 0;
    for (const b of billsFiltered) {
      totalAmount += round2(b.totalAmount);
      paidAmount += round2(b.paidAmount);
      balanceAmount += round2(b.balanceAmount);
    }
    return {
      count: billsFiltered.length,
      totalAmount: round2(totalAmount),
      paidAmount: round2(paidAmount),
      balanceAmount: round2(balanceAmount),
    };
  }, [billsFiltered]);

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const b of bills) {
      const cid = String(b.customerId || "").trim();
      if (!cid) continue;
      const nm = String(b.customerName || "").trim();
      if (nm) m.set(cid, nm);
    }
    return m;
  }, [bills]);

  /** Unique customers from bills — for selects (value is internal key, never shown). */
  const customerPickOptions = useMemo(() => {
    const m = new Map();
    for (const b of billsInBranch) {
      const cid = String(b.customerId || "").trim();
      if (!cid) continue;
      const nm = String(b.customerName || "").trim() || "Customer";
      if (!m.has(cid)) m.set(cid, nm);
    }
    return Array.from(m.entries())
      .map(([customerKey, name]) => ({ customerKey, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [billsInBranch]);

  const paymentsFiltered = useMemo(() => {
    if (!q) return paymentsInBranch;
    return paymentsInBranch.filter((p) => {
      const cid = String(p.customerId || "").trim();
      const cname = customerNameById.get(cid) || "";
      const hay = `${p.id} ${cid} ${cname} ${p.note || ""} ${p.method || ""} ${allocSummary(p, billById)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [paymentsInBranch, q, customerNameById, billById]);

  const paymentsFilteredAmountSum = useMemo(
    () => round2(paymentsFiltered.reduce((s, p) => s + round2(p.amount), 0)),
    [paymentsFiltered]
  );

  const notesFiltered = useMemo(() => {
    if (!q) return notesInBranch;
    return notesInBranch.filter((n) => {
      const hay = `${n.id} ${n.customerId} ${n.customerName} ${n.note || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [notesInBranch, q]);

  /** Bills for allocation picker (same customer as payment when set). */
  const payModalAllocOptions = useMemo(() => {
    if (!payModal) return [];
    const cid = String(payModal.customerId || "").trim();
    const inBranch = (b) => creditBillBranch(b) === branch;
    if (!cid) return bills.filter(inBranch);
    return bills.filter((b) => String(b.customerId || "").trim() === cid && inBranch(b));
  }, [payModal, bills, branch]);

  const openBill = (b) => {
    setBillModal({
      id: b.id,
      customerId: b.customerId || "",
      customerName: b.customerName || "",
      phone: b.phone || "",
      voucherNo: b.voucherNo || "",
      totalAmount: String(round2(b.totalAmount)),
      paidAmount: String(round2(b.paidAmount)),
      invoiceDate: isoDateVal(b.invoiceDate),
      dueDate: isoDateVal(b.dueDate),
      note: b.note || "",
      careOf: b.careOf || "",
      manual: Boolean(b.manual),
      source: b.source || "manual",
      invoiceId: b.invoiceId || "",
      branch: creditBillBranch(b),
    });
  };

  const saveBill = async () => {
    if (!billModal) return;
    setSaving(true);
    try {
      await masterSaveCreditBill(billModal.id, {
        customerId: billModal.customerId,
        customerName: billModal.customerName,
        phone: billModal.phone,
        voucherNo: billModal.voucherNo,
        totalAmount: parseFloat(billModal.totalAmount),
        paidAmount: parseFloat(billModal.paidAmount),
        invoiceDate: billModal.invoiceDate,
        dueDate: billModal.dueDate,
        note: billModal.note,
        careOf: billModal.careOf,
        manual: billModal.manual,
        source: billModal.source,
        invoiceId: billModal.invoiceId,
        branch: billModal.branch,
      });
      toast.success("Bill saved.");
      setBillModal(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const delBill = async (id) => {
    if (!window.confirm("Delete this bill? This cannot be undone.")) return;
    setSaving(true);
    try {
      await masterDeleteCreditBill(id);
      toast.success("Bill deleted.");
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const openPay = (p) => {
    const merged = mergeAllocationsByBillId(Array.isArray(p.allocations) ? p.allocations : []);
    const allocationRows =
      merged.length > 0
        ? merged.map((a, i) => ({
            key: `r-${p.id}-${i}`,
            billId: a.billId,
            amount: String(round2(a.amount)),
          }))
        : [{ key: `r-${p.id}-0`, billId: "", amount: "" }];
    setPayModal({
      id: p.id,
      customerId: p.customerId || "",
      note: p.note || "",
      method: p.method || "manual",
      payMode: p.payMode === "card_upi" ? "card_upi" : "cash",
      paymentDate: paymentDateInputValue(p) || todayYMD(),
      allocationRows,
    });
  };

  const addPayAllocRow = () => {
    setPayModal((s) => ({
      ...s,
      allocationRows: [...(s.allocationRows || []), { key: `r-new-${Date.now()}`, billId: "", amount: "" }],
    }));
  };

  const removePayAllocRow = (key) => {
    setPayModal((s) => ({
      ...s,
      allocationRows: (s.allocationRows || []).filter((r) => r.key !== key),
    }));
  };

  const updatePayAllocRow = (key, field, value) => {
    setPayModal((s) => ({
      ...s,
      allocationRows: (s.allocationRows || []).map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    }));
  };

  const savePay = async () => {
    if (!payModal) return;
    setSaving(true);
    try {
      const rows = (payModal.allocationRows || [])
        .map((r) => ({
          billId: String(r.billId || "").trim(),
          amount: round2(parseFloat(r.amount)),
        }))
        .filter((r) => r.billId && Number.isFinite(r.amount) && r.amount > 0);
      const merged = mergeAllocationsByBillId(rows);
      if (!merged.length) {
        toast.error("Choose a bill and a positive amount on each line.");
        setSaving(false);
        return;
      }
      const amount = round2(merged.reduce((s, a) => s + a.amount, 0));
      await masterReplaceCreditPayment(payModal.id, {
        customerId: payModal.customerId.trim(),
        amount,
        allocations: merged,
        note: payModal.note,
        method: payModal.method,
        payMode: payModal.payMode,
        paymentDate: String(payModal.paymentDate || "").trim(),
      });
      toast.success("Payment and allocations saved.");
      setPayModal(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const delPay = async (id) => {
    if (
      !window.confirm(
        "Delete this payment receipt? Amounts will be reversed from the allocated bills. This cannot be undone."
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await masterDeleteCreditPayment(id);
      toast.success("Payment deleted and bills updated.");
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const openNote = (n) => {
    setNoteModal({
      id: n.id,
      customerId: n.customerId || "",
      customerName: n.customerName || "",
      note: n.note || "",
      lastContactDate: isoDateVal(n.lastContactDate) || String(n.lastContactDate || "").slice(0, 10),
      nextActionDate: isoDateVal(n.nextActionDate) || String(n.nextActionDate || "").slice(0, 10),
    });
  };

  const saveNote = async () => {
    if (!noteModal) return;
    setSaving(true);
    try {
      await masterUpdateCreditAuditNote(noteModal.id, {
        customerId: noteModal.customerId,
        customerName: noteModal.customerName,
        note: noteModal.note,
        lastContactDate: noteModal.lastContactDate,
        nextActionDate: noteModal.nextActionDate,
      });
      toast.success("Note saved.");
      setNoteModal(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const delNote = async (id) => {
    if (!window.confirm("Delete this audit note?")) return;
    setSaving(true);
    try {
      await masterDeleteCreditAuditNote(id);
      toast.success("Deleted.");
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addAuditNoteForCustomer({
        customerId: newNote.customerId.trim(),
        customerName: newNote.customerName.trim(),
        note: newNote.note.trim(),
        lastContactDate: newNote.lastContactDate,
        nextActionDate: newNote.nextActionDate,
      });
      toast.success("Note added.");
      setNewNote({ customerId: "", customerName: "", note: "", lastContactDate: "", nextActionDate: "" });
      await loadAll();
    } catch (e) {
      toast.error(e?.message || "Add failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-7xl px-3 pb-24 pt-4 sm:px-4">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Masters</p>
            <h1 className="text-xl font-bold text-slate-900">Credit / Due — data maintenance</h1>
            <p className="mt-1 max-w-3xl text-xs text-slate-600">
              Maintain Udhar credit bills, payment receipts, and follow-up audit notes. On the Payments tab you can edit which
              bills each receipt applies to. Deleting a payment reverses those bill balances. You can also record receipts from{" "}
              <Link className="text-indigo-600 underline" to="/credit-due/receive">Receive payment</Link>.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <CreditBranchSelector value={branch} onChange={setBranch} options={visibleBranches} />
            <button
              type="button"
              onClick={loadAll}
              disabled={loading}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <p className="mb-3 text-xs text-slate-600">
          Tab counts and tables follow the branch above. Bills without a branch field are treated as{" "}
          <strong className="font-semibold text-slate-800">Main</strong>.
        </p>

        <div className="mb-3 flex flex-wrap gap-2 border-b border-slate-200 pb-2">
          {TABS.map((t) => {
            const count =
              t.id === "bills"
                ? billsInBranch.length
                : t.id === "payments"
                  ? paymentsInBranch.length
                  : notesInBranch.length;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                  tab === t.id ? "bg-indigo-600 text-white shadow" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                {t.label}{" "}
                <span className="tabular-nums opacity-90">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="mb-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, voucher, note…"
            className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {loading && !bills.length ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : tab === "bills" ? (
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase text-slate-600">
                <tr>
                  <th className="p-2">Bill</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">Paid</th>
                  <th className="p-2 text-right">Bal</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Manual</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {billsFiltered.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="p-2">
                      <div className="font-semibold text-slate-900">{b.voucherNo || "—"}</div>
                      <div className="text-sm text-slate-800">{b.customerName || "—"}</div>
                      {b.phone ? <div className="text-[11px] text-slate-500">{b.phone}</div> : null}
                    </td>
                    <td className="p-2 text-right tabular-nums">₹{round2(b.totalAmount).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right tabular-nums">₹{round2(b.paidAmount).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right font-semibold tabular-nums">₹{round2(b.balanceAmount).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-xs">{b.status}</td>
                    <td className="p-2 text-xs">{b.manual ? "yes" : "no"}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button type="button" className="text-indigo-600 font-semibold hover:underline" onClick={() => openBill(b)}>
                        Edit
                      </button>
                      <span className="mx-1 text-slate-300">|</span>
                      <button
                        type="button"
                        className="text-rose-600 font-semibold hover:underline disabled:opacity-40"
                        disabled={saving}
                        onClick={() => delBill(b.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {billsFilteredTotals.count > 0 ? (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold text-slate-900">
                    <td className="p-2 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                      Totals ({billsFilteredTotals.count} bill{billsFilteredTotals.count === 1 ? "" : "s"})
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      ₹{billsFilteredTotals.totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      ₹{billsFilteredTotals.paidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2 text-right tabular-nums text-indigo-950">
                      ₹{billsFilteredTotals.balanceAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2" colSpan={3} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
            {billsFiltered.length === 0 && <p className="p-6 text-center text-sm text-slate-600">No bills.</p>}
          </div>
        ) : tab === "payments" ? (
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase text-slate-600">
                <tr>
                  <th className="p-2">When</th>
                  <th className="p-2">Customer</th>
                  <th className="p-2 text-right">Amount</th>
                  <th className="p-2">Method</th>
                  <th className="p-2">Allocations</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentsFiltered.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="p-2 text-xs whitespace-nowrap">{fmtPaymentWhen(p)}</td>
                    <td className="p-2">
                      <div className="font-medium text-slate-900">
                        {customerNameById.get(String(p.customerId || "").trim()) || "—"}
                      </div>
                    </td>
                    <td className="p-2 text-right font-semibold tabular-nums">₹{round2(p.amount).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-xs">
                      {p.method} / {p.payMode || "cash"}
                    </td>
                    <td className="p-2 max-w-md text-xs text-slate-700">{allocSummary(p, billById)}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button type="button" className="text-indigo-600 font-semibold hover:underline" onClick={() => openPay(p)}>
                        Edit
                      </button>
                      <span className="mx-1 text-slate-300">|</span>
                      <button
                        type="button"
                        className="text-rose-600 font-semibold hover:underline disabled:opacity-40"
                        disabled={saving}
                        onClick={() => delPay(p.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {paymentsFiltered.length > 0 ? (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold text-slate-900">
                    <td className="p-2 text-right text-xs font-bold uppercase tracking-wide text-slate-600" colSpan={2}>
                      Total receipts ({paymentsFiltered.length})
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      ₹{paymentsFilteredAmountSum.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2" colSpan={3} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
            {paymentsFiltered.length === 0 && <p className="p-6 text-center text-sm text-slate-600">No payments.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-bold text-slate-900">Add audit note</h2>
              <form onSubmit={addNote} className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Customer
                  <select
                    required
                    value={newNote.customerId}
                    onChange={(e) => {
                      const cid = e.target.value;
                      const name = customerNameById.get(cid) || "";
                      setNewNote((s) => ({ ...s, customerId: cid, customerName: name }));
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Select customer —</option>
                    {customerPickOptions.map((o) => (
                      <option key={o.customerKey} value={o.customerKey}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
                {customerPickOptions.length === 0 ? (
                  <p className="sm:col-span-2 text-xs text-amber-800">
                    No customers found on credit bills yet. Create or refresh bills, then try again.
                  </p>
                ) : null}
                <input
                  type="date"
                  value={newNote.lastContactDate}
                  onChange={(e) => setNewNote((s) => ({ ...s, lastContactDate: e.target.value }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="date"
                  value={newNote.nextActionDate}
                  onChange={(e) => setNewNote((s) => ({ ...s, nextActionDate: e.target.value }))}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <textarea
                  required
                  placeholder="Note *"
                  rows={2}
                  value={newNote.note}
                  onChange={(e) => setNewNote((s) => ({ ...s, note: e.target.value }))}
                  className="sm:col-span-2 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={saving || customerPickOptions.length === 0}
                  className="sm:col-span-2 rounded-lg bg-indigo-600 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Add note
                </button>
              </form>
            </div>

            <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase text-slate-600">
                  <tr>
                    <th className="p-2">Customer</th>
                    <th className="p-2">Note</th>
                    <th className="p-2">Last / next</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notesFiltered.map((n) => (
                    <tr key={n.id} className="border-t border-slate-100">
                      <td className="p-2">
                        <div className="text-sm font-semibold text-slate-900">{n.customerName || "—"}</div>
                      </td>
                      <td className="p-2 max-w-md text-xs text-slate-700 line-clamp-2">{n.note}</td>
                      <td className="p-2 text-[11px] text-slate-600 whitespace-nowrap">
                        {n.lastContactDate || "—"} → {n.nextActionDate || "—"}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <button type="button" className="text-indigo-600 font-semibold hover:underline" onClick={() => openNote(n)}>
                          Edit
                        </button>
                        <span className="mx-1 text-slate-300">|</span>
                        <button
                          type="button"
                          className="text-rose-600 font-semibold hover:underline disabled:opacity-40"
                          disabled={saving}
                          onClick={() => delNote(n.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {notesFiltered.length === 0 && <p className="p-6 text-center text-sm text-slate-600">No notes.</p>}
            </div>
          </div>
        )}

        {billModal && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-xl">
              <h2 className="text-lg font-bold text-slate-900">Edit credit bill</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Customer
                  <select
                    required
                    value={String(billModal.customerId || "").trim()}
                    onChange={(e) => {
                      const v = e.target.value;
                      const nm = customerNameById.get(v) || "";
                      setBillModal((s) => ({ ...s, customerId: v, customerName: nm || s.customerName }));
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Select customer —</option>
                    {(() => {
                      const cid = String(billModal.customerId || "").trim();
                      const inList = customerPickOptions.some((o) => o.customerKey === cid);
                      if (cid && !inList) {
                        return (
                          <option value={cid}>
                            {(billModal.customerName || "").trim() || "Current party"}
                          </option>
                        );
                      }
                      return null;
                    })()}
                    {customerPickOptions.map((o) => (
                      <option key={o.customerKey} value={o.customerKey}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Customer name (as printed)
                  <input
                    value={billModal.customerName}
                    onChange={(e) => setBillModal((s) => ({ ...s, customerName: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Phone
                  <input
                    value={billModal.phone}
                    onChange={(e) => setBillModal((s) => ({ ...s, phone: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Branch
                  <select
                    value={String(billModal.branch || "Main")}
                    onChange={(e) => setBillModal((s) => ({ ...s, branch: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {visibleBranches.map((vb) => (
                      <option key={vb.value} value={vb.value}>
                        {vb.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Voucher / bill no.
                  <input
                    value={billModal.voucherNo}
                    onChange={(e) => setBillModal((s) => ({ ...s, voucherNo: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Total (₹)
                  <input
                    type="number"
                    step="0.01"
                    value={billModal.totalAmount}
                    onChange={(e) => setBillModal((s) => ({ ...s, totalAmount: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-right tabular-nums"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Paid (₹)
                  <input
                    type="number"
                    step="0.01"
                    value={billModal.paidAmount}
                    onChange={(e) => setBillModal((s) => ({ ...s, paidAmount: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-right tabular-nums"
                  />
                </label>
                <p className="sm:col-span-2 text-xs text-slate-600">
                  Balance is computed as total − paid and status is derived. Use only when you know the accounting impact.
                </p>
                <label className="block text-xs font-bold text-slate-700">
                  Invoice date
                  <input
                    type="date"
                    value={billModal.invoiceDate}
                    onChange={(e) => setBillModal((s) => ({ ...s, invoiceDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Due date
                  <input
                    type="date"
                    value={billModal.dueDate}
                    onChange={(e) => setBillModal((s) => ({ ...s, dueDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Source
                  <select
                    value={billModal.source}
                    onChange={(e) => setBillModal((s) => ({ ...s, source: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="manual">manual</option>
                    <option value="invoice">invoice</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 pt-6 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    checked={billModal.manual}
                    onChange={(e) => setBillModal((s) => ({ ...s, manual: e.target.checked }))}
                  />
                  Manual entry flag
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Linked stock invoice (optional)
                  <input
                    value={billModal.invoiceId}
                    onChange={(e) => setBillModal((s) => ({ ...s, invoiceId: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  C/o
                  <input
                    value={billModal.careOf}
                    onChange={(e) => setBillModal((s) => ({ ...s, careOf: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="sm:col-span-2 block text-xs font-bold text-slate-700">
                  Note
                  <textarea
                    rows={2}
                    value={billModal.note}
                    onChange={(e) => setBillModal((s) => ({ ...s, note: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={saveBill}
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setBillModal(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {payModal && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-xl">
              <h2 className="text-lg font-bold text-slate-900">Edit payment and allocations</h2>
              <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-snug">
                Each row updates that bill by the change in amount versus before save. Pick the same customer as on every bill in
                the list. Receipt total is the sum of the amounts below.
              </p>
              <div className="mt-4 space-y-3">
                <label className="block text-xs font-bold text-slate-700">
                  Customer
                  <select
                    value={String(payModal.customerId || "").trim()}
                    onChange={(e) => setPayModal((s) => ({ ...s, customerId: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Select customer —</option>
                    {(() => {
                      const cid = String(payModal.customerId || "").trim();
                      const inList = customerPickOptions.some((o) => o.customerKey === cid);
                      if (cid && !inList) {
                        return (
                          <option value={cid}>
                            {customerNameById.get(cid) || "Customer on this receipt"}
                          </option>
                        );
                      }
                      return null;
                    })()}
                    {customerPickOptions.map((o) => (
                      <option key={o.customerKey} value={o.customerKey}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Payment date
                  <input
                    type="date"
                    value={payModal.paymentDate || ""}
                    onChange={(e) => setPayModal((s) => ({ ...s, paymentDate: e.target.value }))}
                    className="mt-1 w-full max-w-[12rem] rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <span className="mt-1 block text-[10px] font-normal font-medium text-slate-500">
                    Receipt / value date for lists, exports, and daily collection. Clear the field and save to fall back to
                    server save time for ordering.
                  </span>
                </label>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-700">Allocations</span>
                    <button
                      type="button"
                      onClick={addPayAllocRow}
                      className="text-xs font-bold text-indigo-600 hover:underline"
                    >
                      + Add line
                    </button>
                  </div>
                  <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase text-slate-600">
                        <tr>
                          <th className="p-2">Bill</th>
                          <th className="p-2 text-right">Amount (₹)</th>
                          <th className="p-2 w-16" />
                        </tr>
                      </thead>
                      <tbody>
                        {(payModal.allocationRows || []).map((r) => {
                          const opts = payModalAllocOptions;
                          const hasBill = Boolean(String(r.billId || "").trim());
                          const inOptions = hasBill && opts.some((b) => b.id === r.billId);
                          return (
                            <tr key={r.key} className="border-t border-slate-100">
                              <td className="p-2 align-top">
                                <select
                                  value={r.billId}
                                  onChange={(e) => updatePayAllocRow(r.key, "billId", e.target.value)}
                                  className="w-full min-w-[12rem] rounded border border-slate-300 px-2 py-1.5 text-sm"
                                >
                                  <option value="">— Choose bill —</option>
                                  {hasBill && !inOptions ? (
                                    <option value={r.billId}>
                                      {billPartyVoucher(r.billId, billById) || "Bill (refresh if missing)"}
                                    </option>
                                  ) : null}
                                  {opts.map((b) => (
                                    <option key={b.id} value={b.id}>
                                      {billPartyVoucher(b.id, billById)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-2 align-top">
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={r.amount}
                                  onChange={(e) => updatePayAllocRow(r.key, "amount", e.target.value)}
                                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-right text-xs tabular-nums"
                                />
                              </td>
                              <td className="p-2 align-top text-right">
                                {(payModal.allocationRows || []).length > 1 ? (
                                  <button
                                    type="button"
                                    onClick={() => removePayAllocRow(r.key)}
                                    className="text-xs font-semibold text-rose-600 hover:underline"
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-800">
                    Receipt total: ₹
                    {round2(
                      (payModal.allocationRows || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
                    ).toLocaleString("en-IN")}
                  </p>
                </div>
                <label className="block text-xs font-bold text-slate-700">
                  Method
                  <input
                    value={payModal.method}
                    onChange={(e) => setPayModal((s) => ({ ...s, method: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Pay mode
                  <select
                    value={payModal.payMode}
                    onChange={(e) => setPayModal((s) => ({ ...s, payMode: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="cash">cash</option>
                    <option value="card_upi">card_upi</option>
                  </select>
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Note
                  <textarea
                    rows={2}
                    value={payModal.note}
                    onChange={(e) => setPayModal((s) => ({ ...s, note: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={savePay}
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setPayModal(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {noteModal && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
              <h2 className="text-lg font-bold text-slate-900">Edit audit note</h2>
              <div className="mt-4 space-y-3">
                <label className="block text-xs font-bold text-slate-700">
                  Customer
                  <select
                    value={String(noteModal.customerId || "").trim()}
                    onChange={(e) => {
                      const v = e.target.value;
                      const nm = customerNameById.get(v) || "";
                      setNoteModal((s) => ({ ...s, customerId: v, customerName: nm || s.customerName }));
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— Select customer —</option>
                    {(() => {
                      const cid = String(noteModal.customerId || "").trim();
                      const inList = customerPickOptions.some((o) => o.customerKey === cid);
                      if (cid && !inList) {
                        return (
                          <option value={cid}>
                            {(noteModal.customerName || "").trim() || "Current party"}
                          </option>
                        );
                      }
                      return null;
                    })()}
                    {customerPickOptions.map((o) => (
                      <option key={o.customerKey} value={o.customerKey}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Customer name (as printed)
                  <input
                    value={noteModal.customerName}
                    onChange={(e) => setNoteModal((s) => ({ ...s, customerName: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Last contact
                  <input
                    type="date"
                    value={noteModal.lastContactDate}
                    onChange={(e) => setNoteModal((s) => ({ ...s, lastContactDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Next action
                  <input
                    type="date"
                    value={noteModal.nextActionDate}
                    onChange={(e) => setNoteModal((s) => ({ ...s, nextActionDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Note
                  <textarea
                    required
                    rows={3}
                    value={noteModal.note}
                    onChange={(e) => setNoteModal((s) => ({ ...s, note: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={saveNote}
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setNoteModal(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
